import React, { useState } from 'react';
import { X, Laptop, Smartphone, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from '@/hooks/useLanguage';

interface WebsiteModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  thumbnail?: string;        // für normale Portfolio-Items
  websiteUrl?: string;       // für Case Study iFrame
}

export const WebsiteModal = ({
  isOpen,
  onClose,
  title,
  description,
  thumbnail,
  websiteUrl
}: WebsiteModalProps) => {
  const { t } = useLanguage();
  const [activeDevice, setActiveDevice] = useState<'desktop' | 'mobile'>('desktop');

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl w-full p-0 overflow-hidden rounded-lg">
        <DialogClose className="absolute right-4 top-4 z-10">
          <X className="h-5 w-5 text-muted-foreground hover:text-foreground transition-colors" />
        </DialogClose>

        <DialogTitle className="px-6 pt-6 text-xl font-semibold">
          {title}
        </DialogTitle>

        <div className="px-6 py-4">
          <p className="text-muted-foreground mb-4">{description}</p>

          <Tabs
            defaultValue="desktop"
            className="w-full"
            onValueChange={(value) => setActiveDevice(value as 'desktop' | 'mobile')}
          >
            <TabsList className="grid w-full max-w-[200px] grid-cols-2 mx-auto mb-6">
              <TabsTrigger value="desktop" className="flex items-center gap-2">
                <Laptop className="h-4 w-4" />
                <span>Desktop</span>
              </TabsTrigger>
              <TabsTrigger value="mobile" className="flex items-center gap-2">
                <Smartphone className="h-4 w-4" />
                <span>Mobile</span>
              </TabsTrigger>
            </TabsList>

            {/* Desktop View */}
            <TabsContent value="desktop" className="border rounded-lg p-4 bg-slate-50 flex justify-center">
              <div className="shadow-xl rounded-md overflow-hidden max-w-full" style={{ width: '90%', aspectRatio: '16/9' }}>
                {websiteUrl ? (
                  <iframe
                    src={websiteUrl}
                    title={title}
                    className="w-full h-full"
                    style={{ border: 'none' }}
                  />
                ) : (
                  thumbnail && <img src={thumbnail} alt={title} className="w-full h-full object-cover object-top" />
                )}
              </div>
            </TabsContent>

            {/* Mobile View */}
            <TabsContent value="mobile" className="flex justify-center p-8">
              <div className="shadow-xl rounded-xl overflow-hidden border-8 border-gray-800 relative" style={{ width: '280px', height: '560px' }}>
                {websiteUrl ? (
                  <iframe
                    src={websiteUrl}
                    title={title}
                    className="w-full h-full"
                    style={{ border: 'none' }}
                  />
                ) : (
                  thumbnail && <img src={thumbnail} alt={title} className="w-full h-full object-cover object-top" />
                )}
              </div>
            </TabsContent>
          </Tabs>

          {/* Optional Visit Website Button nur anzeigen, wenn websiteUrl existiert */}
          {websiteUrl && (
            <div className="flex justify-center mt-6">
              <Button onClick={() => window.open(websiteUrl, '_blank')} className="gap-2">
                <ExternalLink className="h-4 w-4" />
                {t('Visit Website')}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};